<template>
  <div v-lazy-load="shouldUseLazyLoad" class="trait-preview-container">
    <div
      class="body-trait-preview"
      v-if="categoryDisplayType === 'body'"
      :style="bodyTraitPreviewStyle"
      :class="{ selected: this.$store.getters.traits[categoryKey] === option.value }"
    >
      <BodyTraitPreview
        v-if="visible"
        @on-select-option="setOption(categoryKey, option.value)"
        :preview-src="getOptionPreview(categoryKey, option.value)"
        :value="option.value"
        :tab-navigation-enabled="tabNavigationEnabled"
      />
    </div>

    <div
      class="head-trait-preview"
      v-if="categoryDisplayType === 'head'"
      :class="{
        selected:
          this.$store.getters.traits[categoryKey] === option.value ||
          (!this.$store.getters.traits[categoryKey] && option.value === -1),
      }"
    >
      <HeadTraitPreview
        v-if="visible"
        @on-select-option="setOption(categoryKey, option.value)"
        :preview-src="getOptionPreview(categoryKey, option.value)"
        :item-value="option.value"
        :tab-navigation-enabled="tabNavigationEnabled"
      />
    </div>

    <div class="eye-trait-preview" v-if="categoryDisplayType === 'pupil'">
      <div
        class="selected-checkmark"
        v-if="this.$store.getters.traits[categoryKey] === option.value"
      >
        <img src="@/assets/attribute-check@3x.png" draggable="false" />
      </div>
      <EyeTraitPreview
        @on-select-option="setOption(categoryKey, option.value)"
        :pupil-tone="option.value"
        :show-iris="this.$store.getters.traits[categoryKey] !== option.value"
        :tab-navigation-enabled="tabNavigationEnabled"
      />
    </div>

    <div
      v-if="isShowFacialFeature"
      class="facial-feature-preview"
      :class="{
        selected: isFacialFeatureSelected,
      }"
    >
      <FacialFeatureTraitPreview
        @on-select-option="setOption(categoryKey, option.group_id)"
        :category-key="categoryKey"
        :option="option.group_id"
        :preview-src="getOptionPreviewFacialFeature(categoryKey, option.group_id)[0]"
        :preview-src-second="
          getOptionPreviewFacialFeature(categoryKey, option.group_id).length > 1
            ? getOptionPreviewFacialFeature(categoryKey, option.group_id)[1]
            : null
        "
        :flip-second="flipSecond"
        :tab-navigation-enabled="tabNavigationEnabled"
      />
    </div>

    <div class="swatch-trait-preview" v-if="categoryDisplayType === 'swatch'">
      <div
        class="selected-checkmark"
        v-if="this.$store.getters.traits[categoryKey] === option.value"
      >
        <img src="@/assets/attribute-check@3x.png" draggable="false" />
      </div>
      <SwatchTraitPreview
        @on-select-option="setOption(categoryKey, option.value)"
        :category-key="categoryKey"
        :color-tone="option.value"
        :is-selected="
          this.$store.getters.traits[categoryKey] === option.value ||
            (option.value === -1 && !this.$store.getters.traits[categoryKey])
        "
        :tab-navigation-enabled="tabNavigationEnabled"
      />
    </div>

    <div class="makeup-trait-preview" v-if="categoryDisplayType === 'makeup'">
      <div
        class="selected-checkmark"
        v-if="this.$store.getters.traits[categoryKey] === option.value"
      >
        <img v-if="option.value !== -1" src="@/assets/attribute-check@3x.png" draggable="false" />
      </div>
      <MakeupTraitPreview
        @on-select-option="setOption(categoryKey, option.value)"
        :category-key="categoryKey"
        :makeup-tone="option.value"
        :is-selected="
          this.$store.getters.traits[categoryKey] === option.value ||
            (option.value === -1 && !this.$store.getters.traits[categoryKey])
        "
        :tab-navigation-enabled="tabNavigationEnabled"
      />
    </div>
    <div
      class="my-closet"
      v-if="categoryDisplayType === 'my-closet'"
      :class="myClosetOutfitSelected(option)"
    >
      <MyClosetPreview
        @on-select-option="setMyClosetOutfitOption(categoryKey, option)"
        :category-key="categoryKey"
        :preview-src="option.image"
        :tab-navigation-enabled="tabNavigationEnabled"
      />
    </div>
    <span v-if="isTraitPromoted" class="badge-dot" />
  </div>
</template>

<script>
import { NO_ROTATION, TRAIT_PREVIEW_NUM_ROWS_ALWAYS_SHOW } from '@/constants';
import { OutfitTypeEnum } from '@/enum/outfit_type';
import { snapchat } from '@/gen/proto/protos';
import { SixthBodyEnum } from '@/enum/sixth_body';
import FacialFeaturesHelper from '@/services/FacialFeaturesHelper';
import DetailedOutfitHelper from '@/services/DetailedOutfitHelper';
import BodyTraitPreview from '@/components/AvatarBuilderUI/TraitPreviews/BodyTraitPreview';
import HeadTraitPreview from '@/components/AvatarBuilderUI/TraitPreviews/HeadTraitPreview';
import EyeTraitPreview from '@/components/AvatarBuilderUI/TraitPreviews/EyeTraitPreview';
import FacialFeatureTraitPreview from '@/components/AvatarBuilderUI/TraitPreviews/FacialFeatureTraitPreview';
import SwatchTraitPreview from '@/components/AvatarBuilderUI/TraitPreviews/SwatchTraitPreview';
import MakeupTraitPreview from '@/components/AvatarBuilderUI/TraitPreviews/MakeupTraitPreview';
import MyClosetPreview from '@/components/AvatarBuilderUI/TraitPreviews/MyClosetPreview';
import SixthBodyLegacyOptions from '@/services/SixthBodyLegacyOptions';
import { LazyLoad } from '@/directives/LazyLoadDirective';
import { TraitActionTypeEnum } from '@/enum/trait_action_type';
import TraitsHelper from '@/services/TraitsHelper';
const Context = snapchat.bitmoji.api.FacialFeatureCategory.Context;

export default {
  name: 'AvatarBuilderTraitPreview',
  props: [
    'categoryDetails', //: Object,
    'categoryKey', //: String,
    'option', //: Object,
    'optionIndex', //: Number,
    'tabNavigationEnabled', //:Boolean
  ],
  components: {
    BodyTraitPreview,
    HeadTraitPreview,
    EyeTraitPreview,
    FacialFeatureTraitPreview,
    SwatchTraitPreview,
    MakeupTraitPreview,
    MyClosetPreview,
  },
  data: function() {
    return {
      visible: false, // Managed by LazyLoad
      observer: null, // Managed by LazyLoad
    };
  },
  methods: {
    getOptionPreview(category, value) {
      const previewType = this.categoryDetails.display_type;
      const rotation = this.categoryDetails.rotation;

      return this.$store.getters.getOptionPreview({ category, value, previewType, rotation });
    },
    getOptionPreviewFacialFeature(category, value) {
      const previewType = this.categoryDetails.display_type;
      const rotation = NO_ROTATION; // we have no rotation
      const previewTypes = [];
      if (FacialFeaturesHelper.FACIAL_FEATURE_CATEGORY_KEYS.includes(category)) {
        const args = FacialFeaturesHelper.optionPreviewArgs(category, value, previewType, rotation);
        for (const arg of args) {
          previewTypes.push(this.$store.getters.getOptionPreview(arg));
        }
      }

      return previewTypes;
    },
    setOption(category, value) {
      const initialTraits = Object.assign({}, this.$store.getters.traits);
      const optionValue = FacialFeaturesHelper.FACIAL_FEATURE_CATEGORY_KEYS.includes(
        this.categoryKey,
      )
        ? +this.option.group_id
        : this.option.value;
      this.isTraitPromoted &&
        this.$store.commit('clearStaleOptionPromotion', {
          category: this.categoryKey,
          optionId: optionValue,
        });
      if (FacialFeaturesHelper.FACIAL_FEATURE_CATEGORY_KEYS.includes(category)) {
        const initialFacialFeatureTraits = Object.assign(
          {},
          this.$store.getters.facialFeatureTraits,
        );
        const colour = FacialFeaturesHelper.getSavedColourIdOrDefault(
          category,
          value,
          this.$store.getters.facialFeatureTraits,
          this.$store.getters.facialFeaturesMetadata,
        );

        this.$store.commit('setFacialFeature', {
          category,
          value,
          colour,
        });
        this.$store.commit('logTraitAction', {
          action: TraitActionTypeEnum.TAP_PIERCING,
        });
        this.$store.commit('updateAvatarActions', {
          initialTraits,
          facialFeatureTraits: initialFacialFeatureTraits,
        });
        return;
      } else {
        this.$store.commit('setTrait', {
          category,
          value,
        });
        this.commitTraitColourOptionIfNecessary(category, value);

        this.$store.commit('logTraitAction', {
          action: TraitActionTypeEnum.TAP_TRAIT,
        });
      }

      if (category === 'mouth') {
        const categoryMetadata = this.$store.getters.categoryMetadata;

        // Reset lipstick_tone to blank if the user selects a lipless mouth
        if (categoryMetadata.lipless_mouths.includes(this.$store.getters.traits['mouth'])) {
          this.$store.commit('setTrait', {
            category: 'lipstick_tone',
            value: -1,
          });
        }
      }

      // Set a default outfit for the avatar if a user selects a sixth body and current outfit does not support it
      if (
        category === 'body' &&
        (value === SixthBodyEnum.MALE || value === SixthBodyEnum.FEMALE) &&
        this.$store.getters.traits.clothing_type !== OutfitTypeEnum.SPLIT &&
        SixthBodyLegacyOptions.nonSixthBodyOutfits[this.$store.getters.traits.outfit]
      ) {
        this.$store.commit('setAvatarToDefaultOutfit');
      }

      this.$store.commit('updateAvatarActions', {
        initialTraits,
      });
    },
    commitTraitColourOptionIfNecessary(category, value) {
      const colourOption = TraitsHelper.getSelectedColourPickerOptionOrDefaultForItem(
        category,
        value,
        this.$store.getters.categories,
        this.$store.getters.categoryMetadata.swatch_sets,
        this.$store.getters.selectedColourOptionsForTraitItems,
      );

      if (colourOption) {
        this.$store.commit('setTraitColourOption', { category, colourOption });
      }
    },
    getClosetOutfitPreview(values) {
      return this.$store.getters.getClosetOutfitPreview({ values });
    },
    myClosetOutfitSelected(value) {
      // adapted from FashionOutfitPreview
      const isSelected =
        value.clothing_type === OutfitTypeEnum.SPLIT
          ? DetailedOutfitHelper.isSelected(this.$store.getters.traits, value.detailed_options)
          : // Required check because a user may have a regular outfit & voltron outfit set in their traits
            this.$store.getters.traits.clothing_type !== OutfitTypeEnum.SPLIT &&
            this.$store.getters.traits.outfit === value.outfit_id;

      return {
        selected: isSelected,
      };
    },
    setMyClosetOutfitOption(category, value) {
      const initialTraits = Object.assign({}, this.$store.getters.traits);

      const outfitValue =
        value.clothing_type === OutfitTypeEnum.SPLIT ? value.detailed_options : value.outfit_id;

      if (value.clothing_type === OutfitTypeEnum.SPLIT) {
        // Add _outfit keys to mimic proto oneof keys
        Object.keys(outfitValue).forEach((key) => {
          outfitValue[`${key}_outfit`] = key;
        });
      }
      this.$store.commit('setOutfit', {
        type: value.clothing_type,
        value: outfitValue,
      });
      this.$store.commit('tuckControlSetForCurrentOutfit');

      if (value.clothing_type === OutfitTypeEnum.SPLIT) {
        // If the user selects a split outfit, we set the stores selected colour palettes for the
        // individual garments to match their outfit. If they scroll to the mix and match
        // categories, the individual garments and the corresponding colours they are
        // wearing will be selected.
        this.$store.commit('setColourPalettesForSelectedOutfitPieces');
      }

      this.$store.commit('updateAvatarActions', {
        initialTraits,
      });
    },
  },
  computed: {
    categoryDisplayType: function() {
      switch (this.categoryDetails.display_type) {
        case 'body-type':
        case 'mannequin-right':
          return 'body';
        case 'glasses':
          return 'head';
        case 'pupil':
          return 'pupil';
        case 'eye':
        case 'eyebrows':
        case 'hair':
        case 'hat':
        case 'head':
        case 'mouth':
        case 'nose':
        case 'beard':
        case 'jaw':
        case 'face-lines':
        case 'ear':
          return 'head';
        case 'makeup':
          return 'makeup';
        case 'mix-and-match':
          return 'mix-and-match';
        case 'my-closet':
          return 'my-closet';
        case 'earring':
        case 'earring-dual':
        case 'earring-left':
        case 'earring-right':
          return 'earring';
        case 'nosering':
          return 'nosering';
        case 'browring-left':
        case 'browring-right':
          return 'browring';
        case 'earaid-left':
        case 'earaid-right':
          return 'earaid';
        case 'mouthring':
          return 'mouthring';
        case 'tonguering':
          return 'tonguering';
        case 'swatch':
          return 'swatch';
      }

      throw new Error(
        `Unsupported display_type ${this.categoryDetails.display_type} for key: ${this.categoryDetails.category_key}`,
      );
    },
    isShowFacialFeature: function() {
      return FacialFeaturesHelper.FACIAL_FEATURE_CATEGORY_KEYS.includes(this.categoryKey);
    },
    shouldUseLazyLoad: function() {
      // Do not use lazy loading for the first and last `numRows` rows of the category. The number
      // of rows shown is based on screen size and 3 rows of options are shown on most devices.
      const numOptionsToAlwaysShow =
        this.categoryDetails.column_count * TRAIT_PREVIEW_NUM_ROWS_ALWAYS_SHOW;

      // Using the category's column count we can calculate the indices of the options that will not use lazy loading.
      return this.optionIndex >= numOptionsToAlwaysShow;
    },
    bodyTraitPreviewStyle: function() {
      // Do not set the height for body category
      if (this.categoryKey === 'body') {
        return {};
      }
      return {
        height: '40vh',
      };
    },
    isTraitPromoted: function() {
      const value = FacialFeaturesHelper.FACIAL_FEATURE_CATEGORY_KEYS.includes(this.categoryKey)
        ? +this.option.group_id
        : this.option.value;
      return this.$store.getters.isOptionPromoted(this.categoryKey, value);
    },
    isFacialFeatureSelected: function() {
      if (this.option.group_id === '-1') {
        const selectedFacialFeatureTraitsForCategory = FacialFeaturesHelper.getCategoryTraitKeys(
          this.categoryKey,
          this.$store.getters.traits,
        );

        if (selectedFacialFeatureTraitsForCategory.length) {
          return false;
        }
        return true;
      }
      const params = FacialFeaturesHelper.getFacialFeaturePreviewQueryParameters(
        this.$store.getters.facialFeaturesMetadata,
        this.categoryKey,
        this.option.group_id,
      );
      const paramsKeys = Object.keys(params);
      const numberofPiercingsWearing = FacialFeaturesHelper.numberofPiercingsWearing(
        this.$store.getters.traits,
        this.categoryKey,
      );

      if (paramsKeys.length !== numberofPiercingsWearing[this.categoryKey]) {
        return false;
      }
      for (const param of paramsKeys) {
        if (this.$store.getters.traits[param] !== params[param]) {
          return false;
        }
      }

      return true;
    },
    flipSecond() {
      return this.categoryKey === FacialFeaturesHelper.getKeyFromContext(Context.DUAL);
    },
  },
  directives: {
    LazyLoad,
  },
};
</script>

<style scoped>
.body-trait-preview,
.head-trait-preview,
.eye-trait-preview,
.facial-feature-preview,
.my-closet {
  cursor: pointer;
  border-radius: 10px;
}

.body-trait-preview {
  width: 90%;
  margin: 0 auto;
}

.head-trait-preview {
  height: 100%;
  padding: 5px;
  /*
    Using
      display: flex;
      align-items: center;
      justify-content: center;
    here causes https://jira.sc-corp.net/browse/BM-6254
  */
  /* Having "position: relative;" here causes https://jira.sc-corp.net/browse/BM-5913 */
}
.facial-feature-preview {
  height: 95%;
  width: 90%;
  margin: 0 0 0 5%;
  background: #f9fbfc;
}

.eye-trait-preview {
  height: 100px;
  width: 90%;
  margin: 0 auto;
  display: flex;
  box-sizing: border-box;
  padding: 5%;
  align-items: center;
  justify-content: center;
  position: relative;
}

.makeup-trait-preview,
.swatch-trait-preview {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border-radius: 10px;
}

.body-trait-preview:active,
.head-trait-preview:active,
.eye-trait-preview:active,
.facial-feature-preview:active,
.makeup-trait-preview:active,
.my-closet:active,
.swatch-trait-preview:active {
  transform: scale(0.9);
  transition-duration: 0.1s;
}

.my-closet {
  height: 150px;
  padding: 10%;
  background: #f9fbfc;
}

.selected-checkmark {
  position: absolute;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selected-checkmark img {
  width: 50%;
}

.selected,
.selected:hover {
  /*
  `background-color: #0baaff20;`
  The notation of RRGGBBAA is the hex representation of the colour plus opacity. However
  this notation is not supported in newer versions of Chrome: https://stackoverflow.com/a/45482395.
  Therefore we must use the 'rbga()' notation instead with the decimal converted values.
  */
  background-color: #e6f6ff;
  box-shadow: inset 0 0 0 2px #0baaff, inset 0 0 0 4px #ffffff;
  -webkit-box-shadow: inset 0 0 0 2px #0baaff, inset 0 0 0 4px #ffffff;
}

@media (hover: hover) {
  .body-trait-preview:hover,
  .head-trait-preview:hover,
  .eye-trait-preview:hover,
  .facial-feature-preview:hover,
  .my-closet:hover {
    box-shadow: inset 0 0 0 2px rgba(217, 217, 217, 0.17);
  }
}

@media (prefers-color-scheme: dark) {
  .selected,
  .selected:hover {
    background-color: #1f1f1f;
    box-shadow: inset 0 0 0 2px #0baaff, inset 0 0 0 4px #121212;
    -webkit-box-shadow: inset 0 0 0 2px #0baaff, inset 0 0 0 4px #121212;
  }
  .my-closet {
    background: #1e1e1e;
  }
  .facial-feature-preview {
    background: #1f1f1f;
  }
}

.trait-preview-container {
  position: relative;
}

.badge-dot {
  height: 8px;
  width: 8px;
  background-color: #0a92ff;
  border-radius: 50%;
  position: absolute;
  top: 4px;
  right: 4px;
  margin: 0;
}
</style>
